import React, { useEffect, useState } from "react";
import { Container, Grid, Typography, Box, Paper, List, ListItem, ListItemButton, ListItemText, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import LinkIcon from '@mui/icons-material/Link';
import "./FAQ.css";
import faqData from './faqData.json';

const FAQ = () => {
    const [activeSection, setActiveSection] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const copyLinkToClipboard = (qaId) => {
        const url = `${window.location.origin}${window.location.pathname}#${qaId}`;
        navigator.clipboard.writeText(url).then(() => {
            setSnackbarOpen(true);
        }).catch((err) => {
            console.error('Failed to copy link: ', err);
        });
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    useEffect(() => {
        // Handle hash in URL on initial load
        if (window.location.hash) {
            const id = window.location.hash.substring(1);
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const sections = faqData.map(section => ({
                id: section.id,
                element: document.getElementById(section.id)
            }));

            const scrollPosition = window.scrollY + 150;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section.element && section.element.offsetTop <= scrollPosition) {
                    setActiveSection(section.id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Call once to set initial active section
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <Helmet>
                <title>FAQ - Hockey Pipeline</title>
                <meta name="description" content="Frequently asked questions about Hockey Pipeline - NHL prospects statistics and rankings website" />
                <link rel="canonical" href="https://hockeypipeline.com/faq" />
            </Helmet>
            <Container className="container top-container faq-container">
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h4" component="div" className="page-title">
                            Frequently Asked Questions
                        </Typography>
                        <div className="title-underline" />
                    </Grid>

                    <Grid item xs={12} md={3} className="toc-grid">
                    <Paper className="toc-box" elevation={3}>
                        <Typography variant="h6" className="toc-title">
                            Quick Navigation
                        </Typography>
                        <List className="toc-list">
                            {faqData.map((section) => (
                                <ListItem key={section.id} disablePadding>
                                    <ListItemButton 
                                        className={activeSection === section.id ? "toc-item active" : "toc-item"}
                                        onClick={() => {
                                            const element = document.getElementById(section.id);
                                            if (element) {
                                                const offset = 100;
                                                const elementPosition = element.getBoundingClientRect().top;
                                                const offsetPosition = elementPosition + window.pageYOffset - offset;
                                                
                                                window.scrollTo({
                                                    top: offsetPosition,
                                                    behavior: 'smooth'
                                                });
                                                
                                                window.history.pushState(null, '', `#${section.id}`);
                                            }
                                        }}
                                    >
                                        <ListItemText primary={section.title} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Box className="faq-content">
                        {faqData.map((section) => (
                            <Box key={section.id} id={section.id} className="faq-section">
                                <Box className="section-header">
                                    <Typography variant="h5" className="section-title" id={`section-${section.id}`}>
                                        {section.title}
                                    </Typography>
                                    <Tooltip title="Copy link to this section">
                                        <IconButton 
                                            className="copy-link-btn section-copy-btn"
                                            size="small"
                                            onClick={() => copyLinkToClipboard(section.id)}
                                            aria-label="copy section link"
                                        >
                                            <LinkIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <div className="section-underline" />
                                
                                {section.questions.map((qa) => (
                                    <Box key={qa.id} id={qa.id} className="qa-block">
                                        <Box className="qa-header">
                                            <Typography variant="h6" className="question">
                                                Q: {qa.question}
                                            </Typography>
                                            <Tooltip title="Copy link to this question">
                                                <IconButton 
                                                    className="copy-link-btn"
                                                    size="small"
                                                    onClick={() => copyLinkToClipboard(qa.id)}
                                                    aria-label="copy link"
                                                >
                                                    <LinkIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Typography variant="body1" className="answer">
                                            A: {qa.answer}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
            
            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={3000} 
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Link copied to clipboard!
                </Alert>
            </Snackbar>
        </Container>
        </>
    );
};

export default FAQ;
